// ============================================================================
// 브랜치 생성 모달 (CHG-046: REQ-V46-008)
// ============================================================================

import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCreateIssueBranch } from '../../hooks/useGitHub';
import type { GitHubRepo } from '../../services/githubService';

interface Props {
  projectId: string;
  issueId: string;
  repos: GitHubRepo[];
  onClose: () => void;
}

export default function BranchCreateModal({ projectId, issueId, repos, onClose }: Props) {
  const [repoId, setRepoId] = useState(repos[0]?.repoId ?? '');
  const [sourceBranch, setSourceBranch] = useState('');
  const [customName, setCustomName] = useState('');

  const createMutation = useCreateIssueBranch(projectId, issueId);
  const selectedRepo = repos.find((r) => r.repoId === repoId);

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync({
        repoId,
        sourceBranch: sourceBranch || undefined,
        customName: customName || undefined,
      });
      toast.success('브랜치가 생성되었습니다.');
      onClose();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : '브랜치 생성에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">브랜치 생성</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="size-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Repository</label>
            <select
              value={repoId}
              onChange={(e) => setRepoId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
            >
              {repos.map((r) => (
                <option key={r.repoId} value={r.repoId}>{r.fullName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">소스 브랜치</label>
            <input
              value={sourceBranch}
              onChange={(e) => setSourceBranch(e.target.value)}
              placeholder={selectedRepo?.defaultBranch ?? 'main'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-sm text-gray-400 mt-1">비워두면 기본 브랜치({selectedRepo?.defaultBranch ?? 'main'})를 사용합니다.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">브랜치명 (선택)</label>
            <input
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="자동 생성 (네이밍 패턴 기반)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 font-mono"
            />
            <p className="text-sm text-gray-400 mt-1">비워두면 프로젝트 설정의 네이밍 패턴으로 자동 생성됩니다.</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">취소</button>
          <button
            onClick={handleCreate}
            disabled={!repoId || createMutation.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {createMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            생성
          </button>
        </div>
      </div>
    </div>
  );
}
